import Badge from '@cloudscape-design/components/badge';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { MarketingContainer, SectionHeader } from '@vextura/shell';
import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRICING_ONPREM, type PricingTier } from '../content.js';

/**
 * Pricing — on-premises tiers only. Cloud self-serve was removed 2026-07-09
 * because we're staying on-prem-first while the customer story matures.
 */
export function Pricing(): ReactNode {
  const group = PRICING_ONPREM;
  return (
    <MarketingContainer paddingY="default" id="pricing">
      <SpaceBetween size="xl">
        <SectionHeader
          eyebrow="Pricing"
          headline="On-premises. Your servers, your data."
          subhead="Same platform every tier. One command to install."
        />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>{group.headline}</div>
          <div style={{ color: '#64748b', fontSize: 15 }}>{group.subhead}</div>
        </div>
        <ColumnLayout columns={3} variant="text-grid">
          {group.tiers.map((tier) => (
            <PricingCard key={tier.name} tier={tier} />
          ))}
        </ColumnLayout>
      </SpaceBetween>
    </MarketingContainer>
  );
}

function PricingCard({ tier }: { tier: PricingTier }): ReactNode {
  const navigate = useNavigate();
  const cardStyle = {
    padding: 4,
    borderRadius: 12,
    background: tier.highlighted
      ? 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(168,85,247,0.15))'
      : 'transparent',
    height: '100%',
  };
  return (
    <div style={cardStyle}>
      <Container>
        <SpaceBetween size="m">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 700 }}>{tier.name}</div>
            {tier.highlighted ? <Badge color="blue">Most popular</Badge> : null}
          </div>
          <div>
            <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: -1, lineHeight: 1 }}>
              {tier.price}
            </div>
            {tier.priceSuffix ? (
              <div style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>
                {tier.priceSuffix}
              </div>
            ) : null}
          </div>
          <div style={{ color: '#64748b', minHeight: 42 }}>{tier.tagline}</div>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid',
              gap: 10,
            }}
          >
            {tier.features.map((f) => (
              <li
                key={f}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  fontSize: 14,
                }}
              >
                <span style={{ color: '#10b981', flexShrink: 0, marginTop: 2 }}>✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <Button
            variant={tier.highlighted ? 'primary' : 'normal'}
            fullWidth
            onClick={() => navigate(tier.cta.href)}
          >
            {tier.cta.label}
          </Button>
        </SpaceBetween>
      </Container>
    </div>
  );
}
